/**
 * Icon Selection Utilities
 *
 * Category-scoped selection logic for shared icons in the IconPicker.
 *
 * **Problem**: Icons like "Server" appear in multiple categories (Internet, Intranet).
 * Selecting "External Server" (Internet) highlights "Internal Server" (Intranet)
 * because they share the same icon ID: `infrastructure:Server`.
 *
 * **Solution**: Category-scoped selection keys for shared icons.
 * - Shared icons get scoped: `internet::infrastructure:Server`, `intranet::infrastructure:Server`
 * - Non-shared icons stay plain: `aws:Lambda`
 * - Backward compatible: unscoped keys match in all categories
 *
 * @example
 * ```tsx
 * // User selects "External Server" in Internet tab
 * const selection = scopeIconId('infrastructure:Server', 'internet');
 * // Result: 'internet::infrastructure:Server'
 *
 * // IconGrid in Intranet tab checks selection
 * isIconSelected('internet::infrastructure:Server', 'infrastructure:Server', 'intranet');
 * // Result: false (different category)
 *
 * // IconGrid in Internet tab checks selection
 * isIconSelected('internet::infrastructure:Server', 'infrastructure:Server', 'internet');
 * // Result: true (same category)
 *
 * // Registry lookup needs unscoped ID
 * const iconId = unscopeIconId('internet::infrastructure:Server');
 * // Result: 'infrastructure:Server'
 * ```
 */

/**
 * Icons that appear in multiple categories.
 * These icons get category-scoped selection keys to prevent cross-category highlighting.
 *
 * From categories.config.ts:
 * - internet: includes infrastructure:Server, Computer, Database
 * - intranet: includes infrastructure:Server, Computer, Database
 */
export const SHARED_ICON_IDS = [
  'infrastructure:Server',
  'infrastructure:Computer',
  'infrastructure:Database',
] as const;

/**
 * Check if an icon ID is shared across multiple categories.
 *
 * @param iconId - Icon identifier (e.g., 'infrastructure:Server')
 * @returns true if icon appears in multiple categories
 */
export function isSharedIcon(iconId: string | undefined): boolean {
  if (!iconId) return false;
  return (SHARED_ICON_IDS as readonly string[]).includes(iconId);
}

/**
 * Scope an icon ID with category prefix if it's shared across categories.
 *
 * Non-shared icons return unchanged.
 * Empty category or "all" category returns unscoped ID.
 *
 * @param iconId - Icon identifier (e.g., 'infrastructure:Server')
 * @param category - Current category context (e.g., 'internet', 'intranet', 'all')
 * @returns Scoped ID like 'internet::infrastructure:Server' or unchanged ID
 *
 * @example
 * scopeIconId('infrastructure:Server', 'internet') // 'internet::infrastructure:Server'
 * scopeIconId('aws:Lambda', 'cloud') // 'aws:Lambda' (not shared, unchanged)
 * scopeIconId('infrastructure:Server', 'all') // 'infrastructure:Server' (no scoping in "all" view)
 */
export function scopeIconId(iconId: string, category: string): string {
  if (!category || category === 'all') return iconId;
  if (!isSharedIcon(iconId)) return iconId;
  return `${category}::${iconId}`;
}

/**
 * Remove category scope and source prefix from an icon ID.
 *
 * Used when looking up icons in registry (which uses base IDs without prefixes)
 * or mapping to asset classes.
 *
 * Strips both parts:
 * 1. Category scope: 'internet::', 'intranet::', etc.
 * 2. Source prefix: 'infrastructure:', 'aws:', 'azure:', etc.
 *
 * @param iconId - Potentially scoped and prefixed ID (e.g., 'internet::infrastructure:Server')
 * @returns Base ID without scope or prefix (e.g., 'Server')
 *
 * @example
 * unscopeIconId('internet::infrastructure:Server') // 'Server'
 * unscopeIconId('infrastructure:Server') // 'Server'
 * unscopeIconId('aws:Lambda') // 'Lambda'
 * unscopeIconId('Server') // 'Server' (already base ID)
 */
export function unscopeIconId(iconId: string | undefined): string {
  if (!iconId) return '';

  // Step 1: Remove category scope (internet::, intranet::, etc.)
  const scopeIndex = iconId.indexOf('::');
  const afterCategoryScope =
    scopeIndex === -1 ? iconId : iconId.slice(scopeIndex + 2);

  // Step 2: Remove source prefix (infrastructure:, aws:, azure:, etc.) for legacy registry compatibility
  const colonIndex = afterCategoryScope.indexOf(':');
  if (colonIndex !== -1) {
    return afterCategoryScope.slice(colonIndex + 1); // Return just 'Server', 'Computer', etc.
  }

  return afterCategoryScope;
}

/**
 * Check if an icon is selected in the current category context.
 *
 * Handles three cases:
 * 1. **Scoped selection** (e.g., 'internet::infrastructure:Server'):
 *    - Matches icon ONLY in the same category
 *    - Prevents cross-category highlighting
 *
 * 2. **Unscoped selection** (e.g., 'infrastructure:Server'):
 *    - Legacy/backward compatibility
 *    - Matches icon in ALL categories
 *
 * 3. **Non-shared icons** (e.g., 'aws:Lambda'):
 *    - Category irrelevant, match by ID only
 *
 * @param selectedValue - Currently selected icon ID (may be scoped)
 * @param iconId - Icon ID being checked (always unscoped in grid)
 * @param categoryContext - Current category tab ('internet', 'intranet', 'all', etc.)
 * @returns true if icon should be highlighted as selected
 *
 * @example
 * // Scoped selection matches only in same category
 * isIconSelected('internet::infrastructure:Server', 'infrastructure:Server', 'internet') // true
 * isIconSelected('internet::infrastructure:Server', 'infrastructure:Server', 'intranet') // false
 *
 * // Unscoped selection matches everywhere (backward compat)
 * isIconSelected('infrastructure:Server', 'infrastructure:Server', 'internet') // true
 * isIconSelected('infrastructure:Server', 'infrastructure:Server', 'intranet') // true
 *
 * // Non-shared icons match by ID only
 * isIconSelected('aws:Lambda', 'aws:Lambda', 'cloud') // true
 * isIconSelected('aws:Lambda', 'aws:Lambda', 'all') // true
 */
export function isIconSelected(
  selectedValue: string | undefined,
  iconId: string | undefined,
  categoryContext: string
): boolean {
  // Empty selection or icon ID = not selected
  if (!selectedValue || !iconId) return false;

  // Special case: 'all' category shows all scoped selections as selected
  if (categoryContext === 'all') {
    const unscoped = unscopeIconId(selectedValue);
    const unscopedIconId = unscopeIconId(iconId);
    return unscoped === unscopedIconId;
  }

  // Check if selection is scoped
  const scopeIndex = selectedValue.indexOf('::');

  if (scopeIndex === -1) {
    // Unscoped selection (legacy/backward compat) - match by plain ID
    return selectedValue === iconId;
  }

  // Scoped selection - extract category and compare
  const selectedCategory = selectedValue.slice(0, scopeIndex);
  const selectedIconId = selectedValue.slice(scopeIndex + 2);

  // Match only if both category and icon ID match
  return selectedCategory === categoryContext && selectedIconId === iconId;
}
